/**
 * PostHog analytics event name constants.
 *
 * Single source of truth for all custom event names used across the app.
 * Grouped by page/feature area. Use with the useAnalytics() hook.
 *
 * Naming convention:
 *   {page}_*      (plural)   = page-level actions    (e.g. tasks_filtered, signals_searched)
 *   {entity}_*    (singular) = single-entity actions  (e.g. task_viewed, audience_created)
 *
 * Business domains:
 *   task_*     = /tasks page — person-level prospect actions (SDR working individual leads)
 *   signal_*   = /signals page — company-level intent data (marketing discovering accounts)
 *   audience_* = /audiences page — prospect list management and workflow execution
 */

// -- Tasks page (person-level prospect actions) --
export const TASKS_FILTERED = 'tasks_filtered';
export const TASKS_TAB_CHANGED = 'tasks_tab_changed';
export const TASK_NAVIGATED = 'task_navigated';
export const TASK_VIEWED = 'task_viewed';
export const TASK_DISMISSED = 'task_dismissed';
export const TASK_ASSIGNEE_CHANGED = 'task_assignee_changed';
export const TASK_EMAIL_UPDATED = 'task_email_updated';
export const TASK_PHONE_UPDATED = 'task_phone_updated';
export const TASK_RESTORED = 'task_restored';
export const TASK_MAILBOX_SELECTED = 'task_mailbox_selected';
export const TASK_WORKFLOW_SELECTED = 'task_workflow_selected';
export const TASK_DETAIL_TAB_CHANGED = 'task_detail_tab_changed';
export const TASK_WORKFLOW_TRIGGERED = 'task_workflow_triggered';
export const TASK_CALL_INITIATED = 'task_call_initiated';
// Properties: signal_id, signal_type, signal_source, ai_fit_label, ai_fit_score,
//   company_name, company_domain, company_industry, company_region, company_size,
//   mailbox_id, mailbox_email, workflow_id, workflow_name,
//   persona_id, persona_email, persona_name, persona_title, persona_linkedin_url,
//   persona_matched_title, persona_confidence,
//   email_validation_status, email_finding_status, enrichment_status, has_phone,
//   hubspot_contact_exists, salesforce_contact_exists, assigned_to
// Auto-attached by useAnalytics: workspace_id, workspace_name, user_id, user_role, plan_type
export const TASK_SEQUENCE_SENT = 'task_sequence_sent';
export const TASK_SEQUENCE_PAUSED = 'task_sequence_paused';
export const TASK_SEQUENCE_RESUMED = 'task_sequence_resumed';
export const TASK_SEQUENCE_CANCELLED = 'task_sequence_cancelled';
export const TASK_STEP_SKIPPED = 'task_step_skipped';
export const TASK_STEP_REMOVED = 'task_step_removed';
export const TASK_CONTENT_GENERATED = 'task_content_generated';
export const TASK_DIALER_CALL_STARTED = 'task_dialer_call_started';

// -- AE Today & Actions (GH#4247, opportunity-scoped rep experience) --
export const AE_TODAY_VIEWED = 'ae_today_viewed';

// -- Audiences page --
export const AUDIENCE_CREATION_STARTED = 'audience_creation_started';
export const AUDIENCE_CREATED = 'audience_created';
export const AUDIENCE_DUPLICATED = 'audience_duplicated';
export const AUDIENCE_DELETED = 'audience_deleted';
export const AUDIENCE_EDITED = 'audience_edited';
export const AUDIENCE_CSV_UPLOADED = 'audience_csv_uploaded';
export const AUDIENCE_CSV_AI_MAPPING_USED = 'audience_csv_ai_mapping_used';
// Properties: audience_id, audience_name, audience_type, creation_method,
//   total_count, people_count, workflow_count, first_workflow_name,
//   published_at, derived_status, is_test
//   Production runs also include: batch_size, run_type, is_scheduled
// Auto-attached by useAnalytics: workspace_id, workspace_name, user_id, user_role, plan_type
export const AUDIENCE_WORKFLOW_RUN = 'audience_workflow_run';
export const AUDIENCE_WORKFLOW_CREATED = 'audience_workflow_created';
export const CAMPAIGN_WIZARD_STEP_VIEWED = 'campaign_wizard_step_viewed';
export const CAMPAIGN_WIZARD_COMPLETED = 'campaign_wizard_completed';
export const CAMPAIGNS_GALLERY_SCOPE_CHANGED = 'campaigns_gallery_scope_changed';
export const CAMPAIGNS_GALLERY_CARD_OPENED = 'campaigns_gallery_card_opened';
export const CAMPAIGNS_GALLERY_TAG_FILTERED = 'campaigns_gallery_tag_filtered';
export const CAMPAIGNS_GALLERY_SEARCHED = 'campaigns_gallery_searched';
export const AUDIENCES_FILTERED = 'audiences_filtered';
export const AUDIENCES_SORTED = 'audiences_sorted';
export const AUDIENCES_SEARCHED = 'audiences_searched';
export const CAMPAIGN_RESULTS_OPENED = 'campaign_results_opened';
export const AUDIENCE_WORKFLOW_EDITED = 'audience_workflow_edited';

// -- Signals page --
export const SIGNALS_TAB_CHANGED = 'signals_tab_changed';
export const SIGNALS_FILTERED = 'signals_filtered';
export const SIGNALS_SEARCHED = 'signals_searched';
export const SIGNALS_SORTED = 'signals_sorted';
export const SIGNALS_CSV_EXPORTED = 'signals_csv_exported';
export const SIGNAL_COMPANY_VIEWED = 'signal_company_viewed';
export const SIGNAL_COMPANY_IGNORED = 'signal_company_ignored';
export const SIGNALS_AUDIENCE_SAVED = 'signals_audience_saved';
export const SIGNALS_CSV_UPLOADED = 'signals_csv_uploaded';
export const SIGNALS_FIND_PEOPLE_SEARCHED = 'signals_find_people_searched';
export const SIGNALS_ADVANCED_FILTERS_TOGGLED = 'signals_advanced_filters_toggled';
// mb-frontend#4465 — SDR scope, entity view and "Add to outreach"
export const SIGNALS_SCOPE_CHANGED = 'signals_scope_changed';
export const SIGNALS_ENTITY_VIEW_CHANGED = 'signals_entity_view_changed';
// Accepting a signal fires the SAME event from every screen that offers it —
// the Signals Hub and the SDR dashboard's Today review queue — separated by the
// `surface` property ('signals_hub' | 'today_review_queue'). One name means
// counting acceptance is one query instead of a union somebody forgets half of.
//
// Properties: surface, signal_id, company_domain, enrollment_id, mode, status
//   (Signals Hub also sends assignee_scope + entity_view; Today also sends signal_type,
//   queue_rank, priority_score, priority_band, fit_label, fit_score, age_days.)
//   queue_rank/priority_score/priority_band intentionally match the
//   `gtm_signal_impressions` columns so shown-vs-pursued is a join — see
//   views/dashboard/utils/outreachAnalytics.ts.
// Auto-attached by useAnalytics: workspace_id, workspace_name, user_id, user_role, plan_type
export const SIGNALS_ADDED_TO_OUTREACH = 'signals_added_to_outreach';
export const SIGNALS_BULK_ADDED_TO_OUTREACH = 'signals_bulk_added_to_outreach';
export const SIGNALS_ADD_TO_OUTREACH_FAILED = 'signals_add_to_outreach_failed';
/**
 * Marketing-only workspaces (GTM signals off: Parks Associates, floLIVE, ...) see "Add to outreach"
 * as an upsell instead of a control that fails. Nothing is sent to the server; these record who
 * wanted it. Properties: surface ('single' | 'bulk'), entity_view, selected.
 */
export const OUTREACH_UPSELL_VIEWED = 'outreach_upsell_viewed';
export const OUTREACH_UPSELL_SEE_PLANS_CLICKED = 'outreach_upsell_see_plans_clicked';
/**
 * One-tap Add to outreach (mb-frontend#4638): the tap opens a confirmation and Enroll
 * commits it. `signals_added_to_outreach` fires once, on Enroll, so pursue counts stay
 * comparable with the old single-click path; these three name the other outcomes of a tap.
 */
export const SIGNALS_ADD_TO_OUTREACH_CONFIRMATION_SHOWN = 'signals_add_to_outreach_confirmation_shown';
export const SIGNALS_ADD_TO_OUTREACH_NOT_NOW = 'signals_add_to_outreach_not_now';
export const SIGNALS_OUTREACH_CANCELED = 'signals_outreach_canceled';

/**
 * Full-path outreach tracking (mb-frontend#4638): "a rep pressed Enroll and no email went out"
 * has to be answerable without reading four services' logs.
 *
 * The browser owns intent and UI activity; the server owns truth. These events pair with the
 * server's own `outreach_*` family (mb-server: signalEnrollmentController and
 * EnrollmentAnalyticsJob) and the pair is joined on `enrollment_id`, NOT on the person — a second
 * sign-in identity can be remapped to a canonical users row, which would split a distinct-id
 * join silently. Build funnels aggregated by `enrollment_id`.
 *
 * The click-to-send funnel, in order:
 *   signals_outreach_enroll_clicked   browser: the rep pressed Enroll (BEFORE the request)
 *   signals_added_to_outreach         browser: the API accepted it and returned an intent
 *   outreach_enroll_rejected          server: it refused -- `code` says why, nothing was written
 *   outreach_status_changed           server: the intent moved (finding_people -> ready -> ...)
 *   outreach_person_outcome_changed   server: per person (enrolled / skipped / no channel)
 *   outreach_email_sent               server: an email actually left. The only real ending.
 *
 * A click with no `signals_added_to_outreach` and no `outreach_enroll_rejected` after it is a
 * request that never completed — the browser died, or the network dropped it. That gap was
 * invisible before, because the old instrumentation only fired on success.
 */
export const SIGNALS_OUTREACH_ENROLL_CLICKED = 'signals_outreach_enroll_clicked';
export const SIGNALS_OUTREACH_RETRIED = 'signals_outreach_retried';
/**
 * A status transition as the BROWSER saw it, from polling the intent. Deliberately duplicated
 * with the server's `outreach_status_changed`: this one proves the rep was actually shown the
 * change, which is the difference between "the system recovered" and "the rep watched it fail".
 */
export const SIGNALS_OUTREACH_STATUS_OBSERVED = 'signals_outreach_status_observed';
export const SIGNALS_OUTREACH_TEMPLATE_SELECTED = 'signals_outreach_template_selected';
/** The three recovery actions a dead-end state offers. Did anyone ever take them? */
export const SIGNALS_OUTREACH_CONTACT_ADDED = 'signals_outreach_contact_added';
export const SIGNALS_OUTREACH_CONNECT_CLICKED = 'signals_outreach_connect_clicked';
export const SIGNALS_OUTREACH_RECHECK_CLICKED = 'signals_outreach_recheck_clicked';
/** Board 5's People page — where a rep goes to find out what happened to each person. */
export const SIGNALS_OUTREACH_PEOPLE_VIEWED = 'signals_outreach_people_viewed';
export const SIGNALS_OUTREACH_PEOPLE_EXPORTED = 'signals_outreach_people_exported';
export const SIGNALS_OUTREACH_DISMISSED = 'signals_outreach_dismissed';

// -- AI Context page --
export const AI_CONTEXT_TAB_CHANGED = 'ai_context_tab_changed';
export const AI_CONTEXT_COMPANY_RESEARCH_SAVED = 'ai_context_company_research_saved';
export const AI_CONTEXT_COMPANY_RESEARCH_GENERATED = 'ai_context_company_research_generated';
export const AI_CONTEXT_COLD_CALL_SAVED = 'ai_context_cold_call_saved';
export const AI_CONTEXT_COLD_CALL_GENERATED = 'ai_context_cold_call_generated';
export const AI_CONTEXT_PERSONA_CREATED = 'ai_context_persona_created';
export const AI_CONTEXT_PERSONA_EDITED = 'ai_context_persona_edited';
export const AI_CONTEXT_PERSONA_DELETED = 'ai_context_persona_deleted';
export const AI_CONTEXT_MARKET_INTEL_SAVED = 'ai_context_market_intel_saved';
export const AI_CONTEXT_MARKET_INTEL_GENERATED = 'ai_context_market_intel_generated';
export const AI_CONTEXT_F5BOT_CONFIG_SAVED = 'ai_context_f5bot_config_saved';
export const AI_CONTEXT_JOB_POSTING_CREATED = 'ai_context_job_posting_created';
export const AI_CONTEXT_JOB_POSTING_DELETED = 'ai_context_job_posting_deleted';
export const AI_CONTEXT_JOB_TITLES_GENERATED = 'ai_context_job_titles_generated';
export const AI_CONTEXT_TECHNOLOGIES_GENERATED = 'ai_context_technologies_generated';
export const AI_CONTEXT_SIGNAL_CONFIGS_GENERATED = 'ai_context_signal_configs_generated';
export const AI_CONTEXT_COMMUNITY_MENTIONS_CONFIG_GENERATED = 'ai_context_community_mentions_config_generated';

// -- Results page --
export const RESULTS_DATE_RANGE_CHANGED = 'results_date_range_changed';
export const RESULTS_CAMPAIGN_DRILLED = 'results_campaign_drilled';
export const RESULTS_EXPORTED = 'results_exported';
export const RESULTS_CHANNEL_FILTERED = 'results_channel_filtered';

// -- Settings page --
export const SETTINGS_TAB_CHANGED = 'settings_tab_changed';
export const SETTINGS_PROFILE_PHOTO_UPDATED = 'settings_profile_photo_updated';
export const SETTINGS_EMAIL_SIGNATURE_SAVED = 'settings_email_signature_saved';
export const SETTINGS_SDR_VOICE_SAVED = 'settings_sdr_voice_saved';
export const SETTINGS_VOICEMAIL_UPLOADED = 'settings_voicemail_uploaded';
export const SETTINGS_CALENDAR_SAVED = 'settings_calendar_saved';
export const SETTINGS_CHAMPION_SYNCED = 'settings_champion_synced';
export const SETTINGS_CHAMPION_BULK_UPDATED = 'settings_champion_bulk_updated';
export const SETTINGS_CHAMPION_MANUALLY_ADDED = 'settings_champion_manually_added';
export const SETTINGS_CHAMPION_AUTO_SYNC_TOGGLED = 'settings_champion_auto_sync_toggled';
export const SETTINGS_CUSTOMER_RULES_SAVED = 'settings_customer_rules_saved';
export const SETTINGS_GTM_ASSIGNMENT_SAVED = 'settings_gtm_assignment_saved';
export const SETTINGS_TASK_LOCALIZATION_CHANGED = 'settings_task_localization_changed';
export const SETTINGS_TASK_DRAFT_LANGUAGE_PREFERENCE_CHANGED = 'settings_task_draft_language_preference_changed';
export const SETTINGS_AI_SDR_TOGGLED = 'settings_ai_sdr_toggled';
export const SETTINGS_POUNCE_SAVED = 'settings_pounce_saved';
export const SETTINGS_MEETING_ROUTING_SAVED = 'settings_meeting_routing_saved';
export const SETTINGS_PHONE_NUMBER_CONFIGURED = 'settings_phone_number_configured';
export const SETTINGS_INTENT_CLIENT_SAVED = 'settings_intent_client_saved';
export const SETTINGS_FEATURE_TOGGLED = 'settings_feature_toggled';
export const SETTINGS_DEMO_DATA_GENERATED = 'settings_demo_data_generated';

// -- Integrations page --
export const INTEGRATION_CONNECTED = 'integration_connected';
export const INTEGRATION_ENABLED = 'integration_enabled';
export const INTEGRATION_DISABLED = 'integration_disabled';

// -- Bot / Chatbot --
export const CHATBOT_TRAINING_UPDATED = 'chatbot_training_updated';
export const CHATBOT_HANDOFF_CONFIGURED = 'chatbot_handoff_configured';

// -- Auth --
export const USER_SIGNED_UP = 'user_signed_up';
export const USER_SIGNUP_ATTEMPT_EXISTING = 'user_signup_attempt_existing';

// -- Global --
export const UPGRADE_PLAN_CLICKED = 'upgrade_plan_clicked';

// =====================================================================================
// ACQUISITION → ACTIVATION FUNNEL
// Ordered visitor-journey events spanning the marketing site, auth, and onboarding.
// These power the end-to-end conversion funnel in PostHog / GA / Datadog. Fire them
// through the unified tracker (lib/analytics/track.ts `track()`), NOT the private-only
// useAnalytics hook — most fire on public/auth surfaces where that hook is inert.
// Stage ordering (use as the funnel definition):
//   1 landing_viewed → 2 landing_cta_clicked → 3 pricing_viewed → 4 pricing_cta_clicked
//   → 5 signup_started → 6 signup_submitted → 7 signup_completed → 8 email_verification_sent
//   → 9 email_verified → 10 login_completed → 11 workspace_created → 12 onboarding_started
//   → 13 onboarding_module_selected → 14 trial_checkout_started → 15 trial_started
//   → 16 onboarding_step_completed → 17 onboarding_completed → 18 activated
// =====================================================================================

// Stage 1–2: Landing / marketing (formalizes string literals already used in views/home)
export const LANDING_VIEWED = 'landing_viewed';
export const LANDING_CTA_CLICKED = 'landing_cta_clicked';

// Stage 3–4: Pricing (formalizes pricing_* literals in views/pricing)
export const PRICING_VIEWED = 'pricing_viewed';
export const PRICING_TAB_CHANGED = 'pricing_tab_changed';
export const PRICING_CTA_CLICKED = 'pricing_cta_clicked';
export const PRICING_ESTIMATOR_USED = 'pricing_estimator_used';
export const PRICING_BOOK_DEMO_CLICKED = 'pricing_book_demo_clicked';

// Stage 5–9: Signup + verification
export const SIGNUP_STARTED = 'signup_started'; // first interaction with the signup form / OAuth button
export const SIGNUP_SUBMITTED = 'signup_submitted'; // form submitted (pre-result)
export const SIGNUP_COMPLETED = 'signup_completed'; // account created (200) — the headline conversion
export const EMAIL_VERIFICATION_SENT = 'email_verification_sent';
export const EMAIL_VERIFIED = 'email_verified';

// Stage 10: Auth
export const LOGIN_COMPLETED = 'login_completed';

// Stage 11–17: Workspace + onboarding + billing
export const WORKSPACE_CREATED = 'workspace_created';
export const ONBOARDING_STARTED = 'onboarding_started';
export const ONBOARDING_MODULE_SELECTED = 'onboarding_module_selected'; // sales | marketing (+ plan/tier)
export const TRIAL_CHECKOUT_STARTED = 'trial_checkout_started'; // redirected to Stripe
export const TRIAL_STARTED = 'trial_started'; // returned from Stripe success
export const ONBOARDING_STEP_COMPLETED = 'onboarding_step_completed'; // per wizard step (props: step, flow)
export const ONBOARDING_COMPLETED = 'onboarding_completed'; // wow-complete (props: flow)

// Stage 18: Activation — first real value (lands in /tasks or /dashboard with workspace+plan ready)
export const ACTIVATED = 'activated';
